import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { login, refreshTokens, revokeSession } from '@/features/auth/api'
import { AuthContext, type AuthApi, type AuthPhase } from '@/features/auth/auth-context'
import { canRefresh, endSession, readAccessToken, startSession } from '@/features/auth/session'
import { setAuthBridge } from '@/lib/api/client'
import type { LoginResponse } from '@/lib/api/types'
import { refreshTransport } from '@/lib/env'

type AuthProviderProps = { children: ReactNode }

export function AuthProvider({ children }: AuthProviderProps) {
  const queryClient = useQueryClient()
  const [phase, setPhase] = useState<AuthPhase>(
    refreshTransport === 'cookie' ? 'restoring' : 'anonymous',
  )
  const restored = useRef(false)

  const forgetSession = useCallback(() => {
    endSession()
    setPhase('anonymous')
    queryClient.clear()
  }, [queryClient])

  useEffect(() => {
    setAuthBridge({
      getAccessToken: readAccessToken,
      refreshSession: async () => {
        if (!canRefresh()) {
          return false
        }

        try {
          const tokens = await refreshTokens()
          startSession(tokens)
          return true
        } catch {
          return false
        }
      },
      onSessionExpired: forgetSession,
    })
  }, [forgetSession])

  useEffect(() => {
    if (refreshTransport !== 'cookie' || restored.current) {
      return
    }

    restored.current = true

    const restore = async () => {
      try {
        const tokens = await refreshTokens()
        startSession(tokens)
        setPhase('authenticated')
      } catch {
        setPhase('anonymous')
      }
    }

    void restore()
  }, [])

  const completeChallenge = useCallback(
    (tokens: NonNullable<LoginResponse['tokens']>) => {
      startSession(tokens)
      setPhase('authenticated')
      void queryClient.invalidateQueries()
    },
    [queryClient],
  )

  const signIn = useCallback(
    async (email: string, password: string) => {
      const response = await login(email, password)

      if (response.twoFactorRequired !== true && response.tokens !== undefined) {
        completeChallenge(response.tokens)
      }

      return response
    },
    [completeChallenge],
  )

  const signOut = useCallback(async () => {
    try {
      await revokeSession()
    } finally {
      forgetSession()
    }
  }, [forgetSession])

  const api = useMemo<AuthApi>(
    () => ({ phase, signIn, completeChallenge, signOut }),
    [phase, signIn, completeChallenge, signOut],
  )

  return <AuthContext.Provider value={api}>{children}</AuthContext.Provider>
}
